import { InjectionToken, ɵɵdefineInjectable, ɵɵinject, Injectable, Inject, Optional, NgModule, SkipSelf, Component } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { PlatformLocation, CommonModule } from '@angular/common';
import { diag, SpanStatusCode, SpanKind, trace, propagation, defaultTextMapSetter } from '@opentelemetry/api';
import { WebTracerProvider, StackContextManager } from '@opentelemetry/sdk-trace-web';
import { NoopSpanProcessor, SimpleSpanProcessor, ConsoleSpanExporter, BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { isUrlIgnored, ParentBasedSampler, AlwaysOnSampler, AlwaysOffSampler, TraceIdRatioBasedSampler, W3CTraceContextPropagator, CompositePropagator } from '@opentelemetry/core';
import { SemanticResourceAttributes, SemanticAttributes } from '@opentelemetry/semantic-conventions';
import { Resource } from '@opentelemetry/resources';
import { tap, finalize } from 'rxjs/operators';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http/build/src/platform/browser/OTLPTraceExporter';
import { ZipkinExporter } from '@opentelemetry/exporter-zipkin';
import { B3InjectEncoding, B3Propagator } from '@opentelemetry/propagator-b3';
import { JaegerPropagator } from '@opentelemetry/propagator-jaeger';
import { AWSXRayPropagator } from '@opentelemetry/propagator-aws-xray';
import { NoopTextMapPropagator } from '@opentelemetry/api/build/src/propagation/NoopTextMapPropagator';
import { ZoneContextManager } from '@opentelemetry/context-zone-peer-dep';
import { registerInstrumentations } from '@opentelemetry/instrumentation';

/** OTEL_CONFIG : Config injection */
import * as ɵngcc0 from '@angular/core';
import * as ɵngcc1 from '@angular/common';
const OTEL_CONFIG = new InjectionToken('opentelemetry.config');
/** Logger : injection for a logger compatible */
const OTEL_LOGGER = new InjectionToken('otelcol.logger');
/** custom span */
const OTEL_CUSTOM_SPAN = new InjectionToken('otelcol.custom-span');
const OTEL_INSTRUMENTATION_PLUGINS = new InjectionToken('otelcol.instrumentation.plugins');
const defineConfigProvider = (config, configProvider) => {
    if (config) {
        configProvider = { provide: OTEL_CONFIG, useValue: config };
    }
    else {
        if (configProvider) {
            if (configProvider.provide !== OTEL_CONFIG) {
                throw new Error(`Configuration error. token must be : ${OTEL_CONFIG} ,  your token value is : ${configProvider.provide}`);
            }
        }
        else {
            throw new Error(`Configuration error. you must specify a configuration in config or configProvider`);
        }
    }
    return configProvider;
};

var name = "@jufab/opentelemetry-angular-interceptor";
var version = "1.2.0";
var version$1 = {
	name: name,
	version: version
};

/** injection for a Exporter */
const OTEL_EXPORTER = new InjectionToken('otelcol.exporter');

/** injection for a propagator */
const OTEL_PROPAGATOR = new InjectionToken('otelcol.propagator');

/**
 * OpenTelemetryInterceptor class
 */
class OpenTelemetryHttpInterceptor {
    /**
     * constructor
     *
     * @param config configuration
     * @param exporterService service exporter injected
     * @param propagatorService propagator injected
     * @param logger define logger
     * @param customSpan a customSpan interface to add attributes
     * @param platformLocation encapsulates all calls to DOM APIs
     */
    constructor(config, exporterService, propagatorService, logger, customSpan, platformLocation) {
        this.config = config;
        this.exporterService = exporterService;
        this.propagatorService = propagatorService;
        this.logger = logger;
        this.customSpan = customSpan;
        this.platformLocation = platformLocation;
        /**
         * Log or not body
         */
        this.logBody = false;
        this.tracer = new WebTracerProvider({
            sampler: this.defineProbabilitySampler(this.convertStringToNumber(config.commonConfig.probabilitySampler)),
            resource: Resource.default().merge(new Resource({
                [SemanticResourceAttributes.SERVICE_NAME]: this.config.commonConfig.serviceName,
            })),
        });
        this.insertOrNotSpanExporter();
        this.contextManager = new StackContextManager();
        this.tracer.register({
            propagator: this.propagatorService.getPropagator(),
            contextManager: this.contextManager
        });
        this.logBody = config.commonConfig.logBody;
        diag.setLogger(logger, config.commonConfig.logLevel);
    }
    /**
     * Overide method
     * Interceptor from HttpInterceptor Angular
     *
     * @param request the current request
     * @param next next
     */
    intercept(request, next) {
        var _a;
        if (isUrlIgnored(request.url, (_a = this.config.ignoreUrls) === null || _a === void 0 ? void 0 : _a.urls)) {
            return next.handle(request);
        }
        this.contextManager.disable(); //FIX - reinit contextManager for each http call
        this.contextManager.enable();
        const span = this.initSpan(request);
        const tracedReq = this.injectContextAndHeader(request);
        return next.handle(tracedReq).pipe(tap((event) => {
            span.setAttributes({
                [SemanticAttributes.HTTP_STATUS_CODE]: event.status,
            });
            if (this.logBody && event.body != null) {
                span.addEvent('response', { body: JSON.stringify(event.body) });
            }
            span.setStatus({
                code: SpanStatusCode.UNSET
            });
            this.setCustomSpan(span, request, event);
        }, (event) => {
            span.setAttributes({
                [SemanticAttributes.HTTP_STATUS_CODE]: event.status,
            });
            span.recordException({
                name: event.name,
                message: event.message,
                stack: event.error
            });
            span.setStatus({
                code: SpanStatusCode.ERROR
            });
            this.setCustomSpan(span, request, event);
        }), finalize(() => {
            span.end();
            this.contextManager.disable();
        }));
    }
    /**
     * Get current scheme, hostname and port
     */
    getURL() {
        return this.platformLocation.href;
    }
    /**
     * Initialise a span for a request intercepted
     *
     * @param request request
     */
    initSpan(request) {
        const urlRequest = (request.urlWithParams.startsWith('http')) ? new URL(request.urlWithParams) : new URL(this.getURL());
        const span = this.tracer
            .getTracer(name, version)
            .startSpan(`${urlRequest.protocol.replace(':', '').toUpperCase()} ${request.method.toUpperCase()}`, {
            attributes: {
                [SemanticAttributes.HTTP_METHOD]: request.method,
                [SemanticAttributes.HTTP_URL]: request.urlWithParams,
                [SemanticAttributes.HTTP_HOST]: urlRequest.host,
                [SemanticAttributes.HTTP_SCHEME]: urlRequest.protocol.replace(':', ''),
                [SemanticAttributes.HTTP_TARGET]: urlRequest.pathname + urlRequest.search,
                [SemanticAttributes.HTTP_USER_AGENT]: window.navigator.userAgent
            },
            kind: SpanKind.CLIENT,
        }, this.contextManager.active());
        /*eslint no-underscore-dangle: ["error", { "allow": ["_currentContext"] }]*/
        this.contextManager._currentContext = trace.setSpan(this.contextManager.active(), span);
        return span;
    }
    /**
     * Add header propagator in request and conserve original header
     *
     * @param request request
     */
    injectContextAndHeader(request) {
        const carrier = {};
        propagation.inject(this.contextManager.active(), carrier, defaultTextMapSetter);
        request.headers.keys().map(key => {
            carrier[key] = request.headers.get(key);
        });
        return request.clone({
            setHeaders: carrier,
        });
    }
    /**
     * Verify to insert or not a Span Exporter
     */
    insertOrNotSpanExporter() {
        if (this.exporterService.getExporter() !== undefined) {
            this.insertSpanProcessorProductionMode();
            this.insertConsoleSpanExporter();
        }
        else {
            this.tracer.addSpanProcessor(new NoopSpanProcessor());
        }
    }
    /**
     * Insert in tracer the console span if config is true
     */
    insertConsoleSpanExporter() {
        if (this.config.commonConfig.console) {
            this.tracer.addSpanProcessor(new SimpleSpanProcessor(new ConsoleSpanExporter()));
        }
    }
    /**
     * Insert BatchSpanProcessor in production mode
     * SimpleSpanProcessor otherwise
     */
    insertSpanProcessorProductionMode() {
        var _a, _b, _c, _d;
        const bufferConfig = {
            maxExportBatchSize: this.convertStringToNumber((_a = this.config.batchSpanProcessorConfig) === null || _a === void 0 ? void 0 : _a.maxExportBatchSize),
            scheduledDelayMillis: this.convertStringToNumber((_b = this.config.batchSpanProcessorConfig) === null || _b === void 0 ? void 0 : _b.scheduledDelayMillis),
            exportTimeoutMillis: this.convertStringToNumber((_c = this.config.batchSpanProcessorConfig) === null || _c === void 0 ? void 0 : _c.exportTimeoutMillis),
            maxQueueSize: this.convertStringToNumber((_d = this.config.batchSpanProcessorConfig) === null || _d === void 0 ? void 0 : _d.maxQueueSize)
        };
        this.tracer.addSpanProcessor(this.config.commonConfig.production
            ? new BatchSpanProcessor(this.exporterService.getExporter(), bufferConfig)
            : new SimpleSpanProcessor(this.exporterService.getExporter()));
    }
    /**
     * define the Probability Sampler
     * By Default, it's always (or 1)
     *
     * @param sampleConfig the sample configuration
     */
    defineProbabilitySampler(sampleConfig) {
        if (sampleConfig >= 1) {
            return new ParentBasedSampler({ root: new AlwaysOnSampler() });
        }
        else if (sampleConfig <= 0 || sampleConfig === undefined) {
            return new ParentBasedSampler({ root: new AlwaysOffSampler() });
        }
        else {
            return new ParentBasedSampler({ root: new TraceIdRatioBasedSampler(sampleConfig) });
        }
    }
    /**
     * convert String to Number (or undefined)
     *
     * @param value
     * @returns number or undefined
     */
    convertStringToNumber(value) {
        return value !== undefined ? Number(value) : undefined;
    }
    /**
     * Set custom attributes in span with a CustomSpan
     *
     * @param span
     * @param request
     * @param response
     * @returns Span
     */
    setCustomSpan(span, request, response) {
        return this.customSpan != null ? this.customSpan.add(span, request, response) : span;
    }
}
OpenTelemetryHttpInterceptor.ɵfac = function OpenTelemetryHttpInterceptor_Factory(t) { return new (t || OpenTelemetryHttpInterceptor)(ɵngcc0.ɵɵinject(OTEL_CONFIG), ɵngcc0.ɵɵinject(OTEL_EXPORTER), ɵngcc0.ɵɵinject(OTEL_PROPAGATOR), ɵngcc0.ɵɵinject(OTEL_LOGGER, 8), ɵngcc0.ɵɵinject(OTEL_CUSTOM_SPAN, 8), ɵngcc0.ɵɵinject(ɵngcc1.PlatformLocation)); };
OpenTelemetryHttpInterceptor.ɵprov = ɵɵdefineInjectable({ factory: function OpenTelemetryHttpInterceptor_Factory() { return new OpenTelemetryHttpInterceptor(ɵɵinject(OTEL_CONFIG), ɵɵinject(OTEL_EXPORTER), ɵɵinject(OTEL_PROPAGATOR), ɵɵinject(OTEL_LOGGER, 8), ɵɵinject(OTEL_CUSTOM_SPAN, 8), ɵɵinject(PlatformLocation)); }, token: OpenTelemetryHttpInterceptor, providedIn: "root" });
OpenTelemetryHttpInterceptor.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [OTEL_CONFIG,] }] },
    { type: undefined, decorators: [{ type: Inject, args: [OTEL_EXPORTER,] }] },
    { type: undefined, decorators: [{ type: Inject, args: [OTEL_PROPAGATOR,] }] },
    { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [OTEL_LOGGER,] }] },
    { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [OTEL_CUSTOM_SPAN,] }] },
    { type: PlatformLocation }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(OpenTelemetryHttpInterceptor, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [OTEL_CONFIG]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [OTEL_EXPORTER]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [OTEL_PROPAGATOR]
            }] }, { type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [OTEL_LOGGER]
            }] }, { type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [OTEL_CUSTOM_SPAN]
            }] }, { type: ɵngcc1.PlatformLocation }]; }, null); })();

class OpenTelemetryInterceptorModule {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('OpentelemetryInterceptorModule is already loaded. Import it in the AppModule only');
        }
    }
    static forRoot(config, configProvider) {
        //Interceptor
        const interceptorProvider = {
            provide: HTTP_INTERCEPTORS,
            useClass: OpenTelemetryHttpInterceptor,
            multi: true,
        };
        configProvider = defineConfigProvider(config, configProvider);
        return {
            ngModule: OpenTelemetryInterceptorModule,
            providers: [
                configProvider,
                interceptorProvider,
            ],
        };
    }
}
OpenTelemetryInterceptorModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: OpenTelemetryInterceptorModule });
OpenTelemetryInterceptorModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function OpenTelemetryInterceptorModule_Factory(t) { return new (t || OpenTelemetryInterceptorModule)(ɵngcc0.ɵɵinject(OpenTelemetryInterceptorModule, 12)); }, imports: [[HttpClientModule]] });
OpenTelemetryInterceptorModule.ctorParameters = () => [
    { type: OpenTelemetryInterceptorModule, decorators: [{ type: Optional }, { type: SkipSelf }] }
];
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(OpenTelemetryInterceptorModule, { imports: function () { return [HttpClientModule]; } }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(OpenTelemetryInterceptorModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                imports: [HttpClientModule],
                exports: []
            }]
    }], function () { return [{ type: OpenTelemetryInterceptorModule, decorators: [{
                type: Optional
            }, {
                type: SkipSelf
            }] }]; }, null); })();

/**
 * OtelcolExporterService class
 */
class OtelcolExporterService {
    /**
     * constructor
     *
     * @param config OpenTelemetryConfig
     */
    constructor(config) {
        var _a, _b, _c, _d;
        this.otelcolConfig = {
            url: (_a = config.otelcolConfig) === null || _a === void 0 ? void 0 : _a.url,
            headers: (_b = config.otelcolConfig) === null || _b === void 0 ? void 0 : _b.headers,
            concurrencyLimit: Number((_c = config.otelcolConfig) === null || _c === void 0 ? void 0 : _c.concurrencyLimit),
            timeoutMillis: Number((_d = config.otelcolConfig) === null || _d === void 0 ? void 0 : _d.timeoutMillis)
        };
    }
    /**
     * Return a CollectorExporter with the configuration
     *
     * @return a CollectorExporter
     */
    getExporter() {
        return new OTLPTraceExporter(this.otelcolConfig);
    }
}
OtelcolExporterService.ɵfac = function OtelcolExporterService_Factory(t) { return new (t || OtelcolExporterService)(ɵngcc0.ɵɵinject(OTEL_CONFIG)); };
OtelcolExporterService.ɵprov = ɵɵdefineInjectable({ factory: function OtelcolExporterService_Factory() { return new OtelcolExporterService(ɵɵinject(OTEL_CONFIG)); }, token: OtelcolExporterService, providedIn: "root" });
OtelcolExporterService.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [OTEL_CONFIG,] }] }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(OtelcolExporterService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [OTEL_CONFIG]
            }] }]; }, null); })();

class OtelColExporterModule {
}
OtelColExporterModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: OtelColExporterModule });
OtelColExporterModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function OtelColExporterModule_Factory(t) { return new (t || OtelColExporterModule)(); }, providers: [
        { provide: OTEL_EXPORTER, useClass: OtelcolExporterService },
    ], imports: [[]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(OtelColExporterModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                imports: [],
                providers: [
                    { provide: OTEL_EXPORTER, useClass: OtelcolExporterService },
                ]
            }]
    }], null, null); })();

/**
 * ConsoleSpanExporterService
 * A default span exporter
 */
class ConsoleSpanExporterService {
    /**
     * Return a ConsoleSpanExporter
     *
     * @return SpanExporter
     */
    getExporter() {
        return new ConsoleSpanExporter();
    }
}
ConsoleSpanExporterService.ɵfac = function ConsoleSpanExporterService_Factory(t) { return new (t || ConsoleSpanExporterService)(); };
ConsoleSpanExporterService.ɵprov = ɵɵdefineInjectable({ factory: function ConsoleSpanExporterService_Factory() { return new ConsoleSpanExporterService(); }, token: ConsoleSpanExporterService, providedIn: "root" });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(ConsoleSpanExporterService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], null, null); })();

class ConsoleSpanExporterModule {
}
ConsoleSpanExporterModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: ConsoleSpanExporterModule });
ConsoleSpanExporterModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function ConsoleSpanExporterModule_Factory(t) { return new (t || ConsoleSpanExporterModule)(); }, providers: [
        { provide: OTEL_EXPORTER, useClass: ConsoleSpanExporterService }
    ], imports: [[]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(ConsoleSpanExporterModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                imports: [],
                providers: [
                    { provide: OTEL_EXPORTER, useClass: ConsoleSpanExporterService }
                ]
            }]
    }], null, null); })();

/**
 * ZipkinExporterService class
 */
class ZipkinExporterService {
    /**
     * constructor
     *
     * @param config OpenTelemetryConfig
     */
    constructor(config) {
        var _a, _b;
        this.zipkinConfig = {
            url: (_a = config.zipkinConfig) === null || _a === void 0 ? void 0 : _a.url,
            headers: (_b = config.zipkinConfig) === null || _b === void 0 ? void 0 : _b.headers
        };
    }
    /**
     * Return a ZipkinExporter configured with zipkinConfig field
     *
     * @return SpanExporter
     */
    getExporter() {
        return new ZipkinExporter(this.zipkinConfig);
    }
}
ZipkinExporterService.ɵfac = function ZipkinExporterService_Factory(t) { return new (t || ZipkinExporterService)(ɵngcc0.ɵɵinject(OTEL_CONFIG)); };
ZipkinExporterService.ɵprov = ɵɵdefineInjectable({ factory: function ZipkinExporterService_Factory() { return new ZipkinExporterService(ɵɵinject(OTEL_CONFIG)); }, token: ZipkinExporterService, providedIn: "root" });
ZipkinExporterService.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [OTEL_CONFIG,] }] }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(ZipkinExporterService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [OTEL_CONFIG]
            }] }]; }, null); })();

class ZipkinExporterModule {
}
ZipkinExporterModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: ZipkinExporterModule });
ZipkinExporterModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function ZipkinExporterModule_Factory(t) { return new (t || ZipkinExporterModule)(); }, providers: [
        { provide: OTEL_EXPORTER, useClass: ZipkinExporterService }
    ], imports: [[]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(ZipkinExporterModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                imports: [],
                providers: [
                    { provide: OTEL_EXPORTER, useClass: ZipkinExporterService }
                ]
            }]
    }], null, null); })();

/**
 * NoopSpanExporterService
 * A No-op span exporter
 */
class NoopSpanExporterService {
    /**
     * Return undefined
     *
     * @return SpanExporter
     */
    getExporter() {
        return undefined;
    }
}
NoopSpanExporterService.ɵfac = function NoopSpanExporterService_Factory(t) { return new (t || NoopSpanExporterService)(); };
NoopSpanExporterService.ɵprov = ɵɵdefineInjectable({ factory: function NoopSpanExporterService_Factory() { return new NoopSpanExporterService(); }, token: NoopSpanExporterService, providedIn: "root" });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(NoopSpanExporterService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], null, null); })();

class NoopSpanExporterModule {
}
NoopSpanExporterModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: NoopSpanExporterModule });
NoopSpanExporterModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function NoopSpanExporterModule_Factory(t) { return new (t || NoopSpanExporterModule)(); }, providers: [
        { provide: OTEL_EXPORTER, useClass: NoopSpanExporterService }
    ], imports: [[
            CommonModule
        ]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(NoopSpanExporterModule, { imports: function () { return [CommonModule]; } }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(NoopSpanExporterModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                imports: [
                    CommonModule
                ],
                providers: [
                    { provide: OTEL_EXPORTER, useClass: NoopSpanExporterService }
                ]
            }]
    }], null, null); })();

/**
 * B3PropagatorService
 *
 * Can be a single or multi header.
 *
 * See Configuration for more information
 */
class B3PropagatorService {
    /**
     * Constructor
     *
     * @param config OpenTelemetryConfig
     */
    constructor(config) {
        var _a;
        this.b3PropagatorConfig = {
            injectEncoding: B3PropagatorService.defineB3Encoding((_a = config.b3PropagatorConfig) === null || _a === void 0 ? void 0 : _a.multiHeader)
        };
    }
    /**
     * Define if it's a single or multi header
     *
     * @param value string (0 => single header, 1 => Multi Header)
     * @return B3InjectEncoding
     */
    static defineB3Encoding(value) {
        if (value && '0' === value) {
            return B3InjectEncoding.SINGLE_HEADER;
        }
        return B3InjectEncoding.MULTI_HEADER;
    }
    /**
     * Return an B3Propagator
     *
     * @return TextMapPropagator as B3Propagator
     */
    getPropagator() {
        return new B3Propagator(this.b3PropagatorConfig);
    }
}
B3PropagatorService.ɵfac = function B3PropagatorService_Factory(t) { return new (t || B3PropagatorService)(ɵngcc0.ɵɵinject(OTEL_CONFIG)); };
B3PropagatorService.ɵprov = ɵɵdefineInjectable({ factory: function B3PropagatorService_Factory() { return new B3PropagatorService(ɵɵinject(OTEL_CONFIG)); }, token: B3PropagatorService, providedIn: "root" });
B3PropagatorService.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [OTEL_CONFIG,] }] }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(B3PropagatorService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [OTEL_CONFIG]
            }] }]; }, null); })();

class B3PropagatorModule {
}
B3PropagatorModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: B3PropagatorModule });
B3PropagatorModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function B3PropagatorModule_Factory(t) { return new (t || B3PropagatorModule)(); }, providers: [
        { provide: OTEL_PROPAGATOR, useClass: B3PropagatorService }
    ], imports: [[]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(B3PropagatorModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                imports: [],
                providers: [
                    { provide: OTEL_PROPAGATOR, useClass: B3PropagatorService }
                ]
            }]
    }], null, null); })();

/**
 * W3CTraceContextPropagatorService
 */
class W3CTraceContextPropagatorService {
    /**
     * Return an W3CTraceContextPropagatorService
     *
     * @return TextMapPropagator as W3CTraceContextPropagatorService
     */
    getPropagator() {
        return new W3CTraceContextPropagator();
    }
}
W3CTraceContextPropagatorService.ɵfac = function W3CTraceContextPropagatorService_Factory(t) { return new (t || W3CTraceContextPropagatorService)(); };
W3CTraceContextPropagatorService.ɵprov = ɵɵdefineInjectable({ factory: function W3CTraceContextPropagatorService_Factory() { return new W3CTraceContextPropagatorService(); }, token: W3CTraceContextPropagatorService, providedIn: "root" });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(W3CTraceContextPropagatorService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], null, null); })();

/**
 * JaegerHttpTracePropagatorService
 */
class JaegerHttpTracePropagatorService {
    /**
     * Constructor
     *
     * @param config OpenTelemetryConfig
     */
    constructor(config) {
        var _a;
        this.customHeader = (_a = config.jaegerPropagatorConfig) === null || _a === void 0 ? void 0 : _a.customHeader;
    }
    /**
     * Return an JaegerPropagator
     *
     * @return TextMapPropagator as JaegerPropagator
     */
    getPropagator() {
        return new JaegerPropagator(this.customHeader);
    }
}
JaegerHttpTracePropagatorService.ɵfac = function JaegerHttpTracePropagatorService_Factory(t) { return new (t || JaegerHttpTracePropagatorService)(ɵngcc0.ɵɵinject(OTEL_CONFIG)); };
JaegerHttpTracePropagatorService.ɵprov = ɵɵdefineInjectable({ factory: function JaegerHttpTracePropagatorService_Factory() { return new JaegerHttpTracePropagatorService(ɵɵinject(OTEL_CONFIG)); }, token: JaegerHttpTracePropagatorService, providedIn: "root" });
JaegerHttpTracePropagatorService.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [OTEL_CONFIG,] }] }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(JaegerHttpTracePropagatorService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [OTEL_CONFIG]
            }] }]; }, null); })();

/**
 * AwsXrayPropagatorService
 */
class AwsXrayPropagatorService {
    /**
     * Return an AwsXrayPropagator
     *
     * @return TextMapPropagator as AwsXrayPropagator
     */
    getPropagator() {
        return new AWSXRayPropagator();
    }
}
AwsXrayPropagatorService.ɵfac = function AwsXrayPropagatorService_Factory(t) { return new (t || AwsXrayPropagatorService)(); };
AwsXrayPropagatorService.ɵprov = ɵɵdefineInjectable({ factory: function AwsXrayPropagatorService_Factory() { return new AwsXrayPropagatorService(); }, token: AwsXrayPropagatorService, providedIn: "root" });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(AwsXrayPropagatorService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], null, null); })();

/**
 * CompositePropagatorService
 */
class CompositePropagatorService {
    /**
     * Constructor
     */
    constructor(b3PropagatorService, w3cTraceContextPropagatorService, jaegerHttpTracePropagatorService, awsXrayPropagatorService) {
        this.b3PropagatorService = b3PropagatorService;
        this.w3cTraceContextPropagatorService = w3cTraceContextPropagatorService;
        this.jaegerHttpTracePropagatorService = jaegerHttpTracePropagatorService;
        this.awsXrayPropagatorService = awsXrayPropagatorService;
    }
    /**
     * Return an CompositePropagator
     *
     * @return TextMapPropagator as CompositePropagator
     */
    getPropagator() {
        return new CompositePropagator({
            propagators: [
                this.b3PropagatorService.getPropagator(),
                this.w3cTraceContextPropagatorService.getPropagator(),
                this.jaegerHttpTracePropagatorService.getPropagator(),
                this.awsXrayPropagatorService.getPropagator()
            ],
        });
    }
}
CompositePropagatorService.ɵfac = function CompositePropagatorService_Factory(t) { return new (t || CompositePropagatorService)(ɵngcc0.ɵɵinject(B3PropagatorService), ɵngcc0.ɵɵinject(W3CTraceContextPropagatorService), ɵngcc0.ɵɵinject(JaegerHttpTracePropagatorService), ɵngcc0.ɵɵinject(AwsXrayPropagatorService)); };
CompositePropagatorService.ɵprov = ɵɵdefineInjectable({ factory: function CompositePropagatorService_Factory() { return new CompositePropagatorService(ɵɵinject(B3PropagatorService), ɵɵinject(W3CTraceContextPropagatorService), ɵɵinject(JaegerHttpTracePropagatorService), ɵɵinject(AwsXrayPropagatorService)); }, token: CompositePropagatorService, providedIn: "root" });
CompositePropagatorService.ctorParameters = () => [
    { type: B3PropagatorService, decorators: [{ type: Inject, args: [B3PropagatorService,] }] },
    { type: W3CTraceContextPropagatorService, decorators: [{ type: Inject, args: [W3CTraceContextPropagatorService,] }] },
    { type: JaegerHttpTracePropagatorService, decorators: [{ type: Inject, args: [JaegerHttpTracePropagatorService,] }] },
    { type: AwsXrayPropagatorService, decorators: [{ type: Inject, args: [AwsXrayPropagatorService,] }] }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(CompositePropagatorService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: B3PropagatorService, decorators: [{
                type: Inject,
                args: [B3PropagatorService]
            }] }, { type: W3CTraceContextPropagatorService, decorators: [{
                type: Inject,
                args: [W3CTraceContextPropagatorService]
            }] }, { type: JaegerHttpTracePropagatorService, decorators: [{
                type: Inject,
                args: [JaegerHttpTracePropagatorService]
            }] }, { type: AwsXrayPropagatorService, decorators: [{
                type: Inject,
                args: [AwsXrayPropagatorService]
            }] }]; }, null); })();

class CompositePropagatorModule {
}
CompositePropagatorModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: CompositePropagatorModule });
CompositePropagatorModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function CompositePropagatorModule_Factory(t) { return new (t || CompositePropagatorModule)(); }, providers: [
        { provide: OTEL_PROPAGATOR, useClass: CompositePropagatorService }
    ], imports: [[]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(CompositePropagatorModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                imports: [],
                providers: [
                    { provide: OTEL_PROPAGATOR, useClass: CompositePropagatorService }
                ]
            }]
    }], null, null); })();

class AwsXrayPropagatorModule {
}
AwsXrayPropagatorModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: AwsXrayPropagatorModule });
AwsXrayPropagatorModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function AwsXrayPropagatorModule_Factory(t) { return new (t || AwsXrayPropagatorModule)(); }, providers: [
        { provide: OTEL_PROPAGATOR, useClass: AwsXrayPropagatorService }
    ], imports: [[]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(AwsXrayPropagatorModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                imports: [],
                providers: [
                    { provide: OTEL_PROPAGATOR, useClass: AwsXrayPropagatorService }
                ]
            }]
    }], null, null); })();

class W3CTraceContextPropagatorModule {
}
W3CTraceContextPropagatorModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: W3CTraceContextPropagatorModule });
W3CTraceContextPropagatorModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function W3CTraceContextPropagatorModule_Factory(t) { return new (t || W3CTraceContextPropagatorModule)(); }, providers: [
        { provide: OTEL_PROPAGATOR, useClass: W3CTraceContextPropagatorService }
    ], imports: [[]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(W3CTraceContextPropagatorModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                imports: [],
                providers: [
                    { provide: OTEL_PROPAGATOR, useClass: W3CTraceContextPropagatorService }
                ]
            }]
    }], null, null); })();

class JaegerHttpTracePropagatorModule {
}
JaegerHttpTracePropagatorModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: JaegerHttpTracePropagatorModule });
JaegerHttpTracePropagatorModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function JaegerHttpTracePropagatorModule_Factory(t) { return new (t || JaegerHttpTracePropagatorModule)(); }, providers: [
        { provide: OTEL_PROPAGATOR, useClass: JaegerHttpTracePropagatorService }
    ], imports: [[]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(JaegerHttpTracePropagatorModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                imports: [],
                providers: [
                    { provide: OTEL_PROPAGATOR, useClass: JaegerHttpTracePropagatorService }
                ]
            }]
    }], null, null); })();

/**
 * NoopHttpTextPropagatorService
 */
class NoopTextMapPropagatorService {
    /**
     * Return an NoopTextMapPropagator
     *
     * @return TextMapPropagator as NoopTextMapPropagator
     */
    getPropagator() {
        return new NoopTextMapPropagator();
    }
}
NoopTextMapPropagatorService.ɵfac = function NoopTextMapPropagatorService_Factory(t) { return new (t || NoopTextMapPropagatorService)(); };
NoopTextMapPropagatorService.ɵprov = ɵɵdefineInjectable({ factory: function NoopTextMapPropagatorService_Factory() { return new NoopTextMapPropagatorService(); }, token: NoopTextMapPropagatorService, providedIn: "root" });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(NoopTextMapPropagatorService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], null, null); })();

class NoopTextMapPropagatorModule {
}
NoopTextMapPropagatorModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: NoopTextMapPropagatorModule });
NoopTextMapPropagatorModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function NoopTextMapPropagatorModule_Factory(t) { return new (t || NoopTextMapPropagatorModule)(); }, providers: [
        { provide: OTEL_PROPAGATOR, useClass: NoopTextMapPropagatorService }
    ], imports: [[]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(NoopTextMapPropagatorModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                imports: [],
                providers: [
                    { provide: OTEL_PROPAGATOR, useClass: NoopTextMapPropagatorService }
                ]
            }]
    }], null, null); })();

/**
 * InstrumentationService.
 * Service for component to add instrumentation.
 */
class InstrumentationService {
    /**
     * Constructor
     *
     * @param config
     * @param exporterService
     * @param propagatorService
     */
    constructor(config, exporterService, propagatorService, instrumentationOptions) {
        this.config = config;
        this.exporterService = exporterService;
        this.propagatorService = propagatorService;
        this.instrumentationOptions = instrumentationOptions;
        /**
         * contextManager
         */
        this.contextManager = new ZoneContextManager();
        this.tracerProvider = new WebTracerProvider({
            sampler: this.defineProbabilitySampler(this.convertStringToNumber(this.config.commonConfig.probabilitySampler)),
            resource: Resource.default().merge(new Resource({
                [SemanticResourceAttributes.SERVICE_NAME]: this.config.commonConfig.serviceName,
            })),
        });
    }
    /**
     * Init instrumentation on init
     */
    initInstrumentation() {
        this.insertOrNotSpanExporter(this.config.commonConfig.production, this.exporterService, this.config.commonConfig.console);
        this.tracerProvider.register({
            contextManager: this.contextManager,
            propagator: this.propagatorService.getPropagator()
        });
        registerInstrumentations({
            instrumentations: this.instrumentationOptions,
            tracerProvider: this.tracerProvider,
        });
    }
    /**
     * Verify to insert or not a Span Exporter
     */
    insertOrNotSpanExporter(production, exporter, console) {
        if (this.exporterService.getExporter() !== undefined) {
            this.insertSpanProcessorProductionMode(production, exporter);
            this.insertConsoleSpanExporter(console);
        }
        else {
            this.tracerProvider.addSpanProcessor(new NoopSpanProcessor());
        }
    }
    /**
     * Insert in tracer the console span if config is true
     *
     * @param console config to insert console span
     */
    insertConsoleSpanExporter(console) {
        if (console) {
            this.tracerProvider.addSpanProcessor(new SimpleSpanProcessor(new ConsoleSpanExporter()));
        }
    }
    /**
     * Insert BatchSpanProcessor in production mode
     * SimpleSpanProcessor otherwise
     *
     * @param boolean production
     * @param IExporter exporter
     */
    insertSpanProcessorProductionMode(production, exporter) {
        this.tracerProvider.addSpanProcessor(production
            ? new BatchSpanProcessor(exporter.getExporter())
            : new SimpleSpanProcessor(exporter.getExporter()));
    }
    /**
     * convert String to Number (or undefined)
     *
     * @param value
     * @returns number or undefined
     */
    convertStringToNumber(value) {
        return value !== undefined ? Number(value) : undefined;
    }
    /**
     * define the Probability Sampler
     * By Default, it's always (or 1)
     *
     * @param sampleConfig the sample configuration
     */
    defineProbabilitySampler(sampleConfig) {
        if (sampleConfig >= 1) {
            return new ParentBasedSampler({ root: new AlwaysOnSampler() });
        }
        else if (sampleConfig <= 0 || sampleConfig === undefined) {
            return new ParentBasedSampler({ root: new AlwaysOffSampler() });
        }
        else {
            return new ParentBasedSampler({ root: new TraceIdRatioBasedSampler(sampleConfig) });
        }
    }
}
InstrumentationService.ɵfac = function InstrumentationService_Factory(t) { return new (t || InstrumentationService)(ɵngcc0.ɵɵinject(OTEL_CONFIG), ɵngcc0.ɵɵinject(OTEL_EXPORTER), ɵngcc0.ɵɵinject(OTEL_PROPAGATOR), ɵngcc0.ɵɵinject(OTEL_INSTRUMENTATION_PLUGINS)); };
InstrumentationService.ɵprov = ɵɵdefineInjectable({ factory: function InstrumentationService_Factory() { return new InstrumentationService(ɵɵinject(OTEL_CONFIG), ɵɵinject(OTEL_EXPORTER), ɵɵinject(OTEL_PROPAGATOR), ɵɵinject(OTEL_INSTRUMENTATION_PLUGINS)); }, token: InstrumentationService, providedIn: "root" });
InstrumentationService.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [OTEL_CONFIG,] }] },
    { type: undefined, decorators: [{ type: Inject, args: [OTEL_EXPORTER,] }] },
    { type: undefined, decorators: [{ type: Inject, args: [OTEL_PROPAGATOR,] }] },
    { type: Array, decorators: [{ type: Inject, args: [OTEL_INSTRUMENTATION_PLUGINS,] }] }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(InstrumentationService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [OTEL_CONFIG]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [OTEL_EXPORTER]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [OTEL_PROPAGATOR]
            }] }, { type: Array, decorators: [{
                type: Inject,
                args: [OTEL_INSTRUMENTATION_PLUGINS]
            }] }]; }, null); })();

/**
 * Otel Web Tracer Component
 */
class OtelWebTracerComponent {
    /**
     * Constructor
     *
     * @param instrumentationService InstrumentationService
     */
    constructor(instrumentationService) {
        this.instrumentationService = instrumentationService;
    }
    /**
     * Init Component
     */
    ngOnInit() {
        this.instrumentationService.initInstrumentation();
    }
}
OtelWebTracerComponent.ɵfac = function OtelWebTracerComponent_Factory(t) { return new (t || OtelWebTracerComponent)(ɵngcc0.ɵɵdirectiveInject(InstrumentationService)); };
OtelWebTracerComponent.ɵcmp = ɵngcc0.ɵɵdefineComponent({ type: OtelWebTracerComponent, selectors: [["otel-instrumentation"]], decls: 0, vars: 0, template: function OtelWebTracerComponent_Template(rf, ctx) { }, encapsulation: 2 });
OtelWebTracerComponent.ctorParameters = () => [
    { type: InstrumentationService }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(OtelWebTracerComponent, [{
        type: Component,
        args: [{
                // eslint-disable-next-line @angular-eslint/component-selector
                selector: 'otel-instrumentation',
                template: ''
            }]
    }], function () { return [{ type: InstrumentationService }]; }, null); })();

class OtelWebTracerModule {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('OtelWebTracerModule is already loaded. Import it in the AppModule only');
        }
    }
    static forRoot(config, configProvider) {
        configProvider = defineConfigProvider(config, configProvider);
        return {
            ngModule: OtelWebTracerModule,
            providers: [
                configProvider,
                InstrumentationService
            ],
        };
    }
}
OtelWebTracerModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: OtelWebTracerModule });
OtelWebTracerModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function OtelWebTracerModule_Factory(t) { return new (t || OtelWebTracerModule)(ɵngcc0.ɵɵinject(OtelWebTracerModule, 12)); } });
OtelWebTracerModule.ctorParameters = () => [
    { type: OtelWebTracerModule, decorators: [{ type: Optional }, { type: SkipSelf }] }
];
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(OtelWebTracerModule, { declarations: [OtelWebTracerComponent], exports: [OtelWebTracerComponent] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(OtelWebTracerModule, [{
        type: NgModule,
        args: [{
                declarations: [OtelWebTracerComponent],
                exports: [OtelWebTracerComponent]
            }]
    }], function () { return [{ type: OtelWebTracerModule, decorators: [{
                type: Optional
            }, {
                type: SkipSelf
            }] }]; }, null); })();

/*
 * Public API Surface of opentelemetry-interceptor
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AwsXrayPropagatorModule, B3PropagatorModule, CompositePropagatorModule, ConsoleSpanExporterModule, JaegerHttpTracePropagatorModule, NoopSpanExporterModule, NoopTextMapPropagatorModule, OTEL_CONFIG, OTEL_CUSTOM_SPAN, OTEL_EXPORTER, OTEL_INSTRUMENTATION_PLUGINS, OTEL_LOGGER, OTEL_PROPAGATOR, OpenTelemetryInterceptorModule, OtelColExporterModule, OtelWebTracerModule, W3CTraceContextPropagatorModule, ZipkinExporterModule, OtelcolExporterService as ɵa, ConsoleSpanExporterService as ɵb, ZipkinExporterService as ɵc, NoopSpanExporterService as ɵd, B3PropagatorService as ɵe, CompositePropagatorService as ɵf, W3CTraceContextPropagatorService as ɵg, JaegerHttpTracePropagatorService as ɵh, AwsXrayPropagatorService as ɵi, NoopTextMapPropagatorService as ɵj, OtelWebTracerComponent as ɵk, InstrumentationService as ɵl };

//# sourceMappingURL=jufab-opentelemetry-angular-interceptor.js.map